"use client";

import { forgotPassword } from "@/app/(auth)/forgot-password/actions";
import { Button, buttonVariants } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { AppRoutes, NavigationConfig } from "@/config/navigation-config";
import { AuthSuccess, AuthSuccessMessages } from "@/lib/errors";
import { cn } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { IconMail } from "@tabler/icons-react";
import Link from "next/link";
import { useTransition } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { ProviderAuthButton } from "./provider-auth-button";

const forgotPasswordSchema = z.object({
  email: z.email({ message: "Please enter a valid email address" }),
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

type PasswordRecoveryModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function PasswordRecoveryModal({
  open,
  onOpenChange,
}: PasswordRecoveryModalProps) {
  const [isPending, startTransition] = useTransition();

  const form = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: "",
    },
  });

  const onSubmit = (data: ForgotPasswordFormData) => {
    startTransition(async () => {
      const formData = new FormData();
      formData.append("email", data.email);
      const result = await forgotPassword(formData);

      if (result.success) {
        toast.success(AuthSuccessMessages[AuthSuccess.CHECK_EMAIL_RESET]);
        form.reset();
        onOpenChange(false);
      } else {
        toast.error(result.error);
      }
    });
  };

  const onClose = (isOpen: boolean) => {
    form.reset();
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogTitle className="sr-only">Reset your password</DialogTitle>
      <DialogContent className="w-full gap-0 px-5 pt-14 pb-6 sm:max-w-sm sm:px-10 sm:py-14">
        <DialogHeader>
          <DialogTitle className="text-center text-2xl font-semibold">
            Reset your password
          </DialogTitle>
          <DialogDescription className="mt-2 text-center">
            Enter your email address and we&apos;ll send you a link to reset
            your password.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="mt-6 space-y-4"
          >
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <div className="relative">
                      <IconMail className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-zinc-400" />
                      <Input
                        {...field}
                        type="email"
                        autoComplete="email"
                        placeholder="E-mail"
                        disabled={isPending}
                        className="border-zinc-200 bg-zinc-50 pl-10 dark:border-zinc-700 dark:bg-zinc-800"
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Link
              href={NavigationConfig.getPath(AppRoutes.SIGNIN)}
              className={cn(
                buttonVariants({ variant: "link" }),
                "text-muted-foreground px-0",
              )}
            >
              Remembered your password?
            </Link>
            <Button
              type="submit"
              className={cn(
                "h-11 w-full font-medium text-white",
                "bg-linear-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700",
                "disabled:cursor-not-allowed disabled:opacity-50",
              )}
              disabled={isPending}
              loading={isPending}
            >
              {isPending ? "Sending..." : "Send Reset Link"}
            </Button>
          </form>
        </Form>

        <ProviderAuthButton disabled={isPending} />
      </DialogContent>
    </Dialog>
  );
}
